import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import {
  FlaskConical, Play, Send, Loader2, Download, RotateCcw,
  Bot, User, Monitor, Clock, Sparkles, Circle,
  Maximize2, Minimize2, ExternalLink, CheckCircle2,
  List, Zap, FileText, Paperclip, X,
  Plus, ChevronDown, MessageSquare, Trash2, Eye,
  Building2, GraduationCap, Shield, MapPin, Users,
  LayoutDashboard, ArrowLeft, Briefcase, Map, AlertTriangle,
  Film, RotateCw, Pause, Square
} from 'lucide-react';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface Attachment {
  id: string; type: 'image' | 'pdf' | 'file'; name: string; size: number; dataUrl?: string;
}

type InteractiveType = 'envMenu' | 'flowList' | 'flowDetail';

interface Interactive {
  type: InteractiveType;
  envId?: string;
  flowId?: string;
}

interface ChatMessage {
  id: string; role: 'user' | 'assistant' | 'system'; content: string; timestamp: Date;
  actionButton?: { label: string; flowId: string };
  attachments?: Attachment[];
  interactive?: Interactive;
}

interface ChatSession {
  id: string; title: string; createdAt: Date; messages: ChatMessage[];
}

interface ManifestPasso {
  ordem: number; acao: string; fala: string;
  elementoLogico: string; texto: string;
  urlContem: string | null; urlFiltro: string | null;
  tempoMs: number | null; zoom: boolean;
}

interface ManifestFlow {
  id: string; titulo: string; descricao: string;
  duracao: string; dificuldade: string;
  topicoId: string; topicoTitulo: string;
  version: string | null; versionHash: string | null;
  versionCount: number; stepCount: number;
  draft: boolean; urlInicial: string | null;
  passos: ManifestPasso[];
}

interface ManifestCategory {
  id: string; nome: string; icon: string;
  color: string; bgColor: string;
  isInterno: boolean; flowCount: number; readyCount: number;
  flows: ManifestFlow[];
}

interface LabManifest {
  generatedAt: string; manifestHash: string;
  totalCategories: number; totalFlows: number;
  totalReady: number; totalDraft: number;
  categories: ManifestCategory[];
}

interface TestExecution {
  id: string; title: string; flowId: string; envId: string;
  user: string; startedAt: Date; duration: number;
  status: 'running' | 'completed' | 'failed';
  thumbDataUrl?: string; videoBlob?: Blob;
  stepCount: number;
  versionHash: string | null;
  version: string | null;
}

type ViewerState = 'idle' | 'connecting' | 'streaming' | 'recording' | 'finished';
type ActiveTab = 'teste' | 'execucoes';

const MAX_TEXT_LENGTH = 2000;

// ─── Ícone por nome ──────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ReactNode> = {
  Shield: <Shield size={16} />,
  Building2: <Building2 size={16} />,
  Briefcase: <Briefcase size={16} />,
  LayoutDashboard: <LayoutDashboard size={16} />,
  GraduationCap: <GraduationCap size={16} />,
  Users: <Users size={16} />,
  User: <User size={16} />,
  MapPin: <MapPin size={16} />,
  Map: <Map size={16} />,
  Circle: <Circle size={16} />,
};

function getIcon(name: string) { return ICON_MAP[name] || <Circle size={16} />; }

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtSize(b: number) { return b < 1024 ? `${b}B` : b < 1048576 ? `${(b / 1024).toFixed(1)}KB` : `${(b / 1048576).toFixed(1)}MB`; }
const fmt = (s: number) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

function loadSessions(): ChatSession[] {
  try { const s = localStorage.getItem('ac_lab_chat_sessions'); if (s) return JSON.parse(s).map((x: any) => ({ ...x, createdAt: new Date(x.createdAt), messages: x.messages.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })) })); } catch (_) {}
  return [];
}
function saveSessions(ss: ChatSession[]) { localStorage.setItem('ac_lab_chat_sessions', JSON.stringify(ss)); }

// ─── Sub-components ──────────────────────────────────────────────────────────

interface ErrorDetailData {
  title: string;
  reason: string;
  flowId?: string;
  errors?: string[];
  rawLogs?: string;
}

function ErrorDetailModal({ error, onClose, onRetry }: { error: ErrorDetailData; onClose: () => void; onRetry?: () => void }) {
  const [copied, setCopied] = useState(false);

  const fullTextToCopy = useMemo(() => {
    let txt = `=== RELATÓRIO DE FALHA - LAB TESTES IA ===\n`;
    txt += `Título: ${error.title}\n`;
    txt += `Motivo: ${error.reason}\n`;
    if (error.flowId) txt += `Roteiro ID: ${error.flowId}\n`;
    if (error.errors && error.errors.length > 0) {
      txt += `\nLista de Erros (${error.errors.length}):\n`;
      error.errors.forEach((e, idx) => {
        txt += `  [${idx + 1}] ${e}\n`;
      });
    }
    if (error.rawLogs) {
      txt += `\nLogs Brutos:\n${error.rawLogs}\n`;
    }
    return txt;
  }, [error]);

  const handleCopy = () => {
    navigator.clipboard.writeText(fullTextToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 580, background: '#0f172a',
        border: '1px solid #7f1d1d', borderRadius: 12, padding: 20,
        boxShadow: '0 20px 50px rgba(0,0,0,0.7)', color: '#f8fafc',
        display: 'flex', flexDirection: 'column', gap: 14
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: '1px solid #1e293b', paddingBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: '#450a0a', border: '1px solid #991b1b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={20} color="#fca5a5" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#fca5a5' }}>{error.title}</h3>
              <p style={{ margin: 0, fontSize: 10, color: '#94a3b8', marginTop: 2 }}>Relatório Detalhado de Erro do Teste</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4 }}><X size={16} /></button>
        </div>

        {/* Motivo Principal */}
        <div style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(153, 27, 27, 0.2)', border: '1px solid #991b1b' }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: '#fca5a5', textTransform: 'uppercase', letterSpacing: 0.5 }}>Motivo da Falha</span>
          <p style={{ margin: 0, fontSize: 11, color: '#fecdd3', marginTop: 4, fontWeight: 600, lineHeight: 1.4 }}>{error.reason}</p>
        </div>

        {/* Lista de Erros (se houver) */}
        {error.errors && error.errors.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#cbd5e1' }}>Detalhamento da Lista de Erros ({error.errors.length}):</span>
            <div style={{ maxHeight: 150, overflowY: 'auto', background: '#020617', border: '1px solid #1e293b', borderRadius: 8, padding: 8 }}>
              {error.errors.map((errItem, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 6, fontSize: 10, color: '#f87171', padding: '4px 0', borderBottom: idx < error.errors!.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <span style={{ fontWeight: 700, color: '#94a3b8' }}>[{idx + 1}]</span>
                  <span style={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errItem}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logs Brutos */}
        {error.rawLogs && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>Log de Saída (Output):</span>
            <pre style={{ margin: 0, maxHeight: 110, overflowY: 'auto', background: '#020617', border: '1px solid #1e293b', borderRadius: 8, padding: 10, fontSize: 9, color: '#94a3b8', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {error.rawLogs}
            </pre>
          </div>
        )}

        {/* Footer com Ações */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid #1e293b' }}>
          <button onClick={handleCopy} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 6, border: '1px solid #334155', background: copied ? '#064e3b' : '#1e293b', color: copied ? '#6ee7b7' : '#cbd5e1', cursor: 'pointer', fontSize: 10, fontWeight: 600, transition: 'all 0.2s' }}>
            {copied ? <><CheckCircle2 size={12} /> Copiado!</> : <><Paperclip size={12} /> Copiar Detalhes</>}
          </button>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #334155', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 10, fontWeight: 600 }}>
              Fechar
            </button>
            {onRetry && (
              <button onClick={() => { onClose(); onRetry(); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 6, border: 'none', background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff', cursor: 'pointer', fontSize: 10, fontWeight: 700 }}>
                <RotateCw size={12} /> Refazer Teste
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ att, onClose }: { att: Attachment; onClose: () => void }) {
  return (<div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
    <div onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', position: 'relative', cursor: 'default' }}>
      <button onClick={onClose} style={{ position: 'absolute', top: -12, right: -12, width: 28, height: 28, borderRadius: '50%', background: '#ef4444', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}><X size={14} /></button>
      {att.type === 'image' && att.dataUrl ? <img src={att.dataUrl} alt={att.name} style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }} /> : <div style={{ padding: 32, background: '#1e293b', borderRadius: 12, textAlign: 'center', minWidth: 300 }}><FileText size={48} color="#94a3b8" style={{ marginBottom: 12 }} /><p style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{att.name}</p><p style={{ color: '#64748b', fontSize: 12 }}>{fmtSize(att.size)}</p></div>}
    </div>
  </div>);
}

function AttachmentBadge({ att, onClick }: { att: Attachment; onClick: () => void }) {
  return (<div onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6, cursor: 'pointer', background: 'rgba(148,163,184,0.1)', border: '1px solid #334155' }}
    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.2)')} onMouseLeave={e => (e.currentTarget.style.background = 'rgba(148,163,184,0.1)')}>
    {att.type === 'image' && att.dataUrl ? <img src={att.dataUrl} alt="" style={{ width: 20, height: 20, borderRadius: 3, objectFit: 'cover' }} /> : att.type === 'pdf' ? <FileText size={12} color="#ef4444" /> : <Paperclip size={12} color="#94a3b8" />}
    <span style={{ fontSize: 10, color: '#94a3b8', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
    <span style={{ fontSize: 9, color: '#475569' }}>{fmtSize(att.size)}</span>
  </div>);
}

// ─── Interactive Chat Components ─────────────────────────────────────────────

function EnvMenuComponent({ categories, onSelect }: { categories: ManifestCategory[]; onSelect: (envId: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 6 }}>
      {categories.map(env => (
        <button key={env.id} onClick={() => onSelect(env.id)} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 10px', borderRadius: 7, border: `1px solid ${env.color}33`,
          background: env.bgColor, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          <span style={{ color: env.color }}>{getIcon(env.icon)}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#e2e8f0' }}>{env.nome} ({env.readyCount})</span>
        </button>
      ))}
    </div>
  );
}

function FlowListComponent({ category, onSelect, onBack }: { category: ManifestCategory; onSelect: (flowId: string) => void; onBack: () => void }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {category.flows.map(flow => (
          <button key={flow.id} onClick={() => onSelect(flow.id)} disabled={flow.draft} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '7px 10px', borderRadius: 7, border: `1px solid ${category.color}33`,
            background: flow.draft ? 'rgba(30,41,59,0.3)' : category.bgColor,
            cursor: flow.draft ? 'not-allowed' : 'pointer', textAlign: 'left',
            transition: 'all 0.15s', opacity: flow.draft ? 0.5 : 1,
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#e2e8f0' }}>
              {flow.draft && '🚧 '}{flow.titulo}
            </span>
            <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
              {flow.version && <span style={{ fontSize: 8, color: category.color, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: `${category.color}20` }}>{flow.version}</span>}
              <span style={{ fontSize: 8.5, color: '#64748b' }}>{flow.duracao || `${flow.stepCount}p`}</span>
            </div>
          </button>
        ))}
      </div>
      <button onClick={onBack} style={{
        marginTop: 6, display: 'flex', alignItems: 'center', gap: 4,
        padding: '5px 10px', borderRadius: 6, border: 'none',
        background: 'rgba(148,163,184,0.1)', color: '#94a3b8',
        cursor: 'pointer', fontSize: 9, fontWeight: 600, width: '100%', justifyContent: 'center',
      }}><ArrowLeft size={10} /> Voltar aos ambientes</button>
    </div>
  );
}

function FlowDetailComponent({ flow, category, onExecute, onBack, busy }: { flow: ManifestFlow; category: ManifestCategory; onExecute: (flowId: string) => void; onBack: () => void; busy: boolean }) {
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ padding: '8px 10px', borderRadius: 8, background: category.bgColor, border: `1px solid ${category.color}33` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
          <span style={{ color: category.color }}>{getIcon(category.icon)}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0' }}>{flow.titulo}</span>
          {flow.version && <span style={{ fontSize: 8, color: category.color, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: `${category.color}20` }}>{flow.version}</span>}
        </div>
        {flow.descricao && <p style={{ fontSize: 9, color: '#c4b5fd', lineHeight: 1.4, whiteSpace: 'pre-wrap', marginBottom: 6 }}>{flow.descricao}</p>}
        <div style={{ display: 'flex', gap: 10, fontSize: 8.5, color: '#94a3b8' }}>
          <span>📋 {flow.stepCount} passo(s)</span>
          {flow.duracao && <span>⏱ {flow.duracao}</span>}
          {flow.dificuldade && <span>📊 {flow.dificuldade}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
        <button onClick={onBack} style={{
          flex: 1, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center',
          padding: '6px 8px', borderRadius: 6, border: '1px solid #334155',
          background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 9, fontWeight: 600,
        }}><ArrowLeft size={10} /> Voltar</button>
        <button onClick={() => onExecute(flow.id)} disabled={busy} style={{
          flex: 2, display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center',
          padding: '6px 8px', borderRadius: 6, border: 'none',
          background: busy ? '#374151' : `linear-gradient(135deg, ${category.color}, ${category.color}cc)`,
          color: '#fff', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 9, fontWeight: 700,
          boxShadow: busy ? 'none' : `0 3px 10px ${category.color}40`,
        }}><Play size={10} /> Executar Teste</button>
      </div>
    </div>
  );
}

// ─── Componente Principal ────────────────────────────────────────────────────

export function MasterLabTestesPage() {
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<LabManifest | null>(null);
  const [manifestLoading, setManifestLoading] = useState(true);

  // ── Desativar PWA / Service Worker para esta rota ──
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        for (const reg of regs) {
          reg.unregister();
        }
      });
    }
  }, []);
  const [activeTab, setActiveTab] = useState<ActiveTab>('teste');
  const [sessions, setSessions] = useState<ChatSession[]>(loadSessions);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(() => { const s = loadSessions(); return s.length > 0 ? s[0].id : null; });
  const [showSessionMenu, setShowSessionMenu] = useState(false);
  const activeSession = useMemo(() => sessions.find(s => s.id === activeSessionId) || null, [sessions, activeSessionId]);
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [previewAtt, setPreviewAtt] = useState<Attachment | null>(null);
  const [errorModalData, setErrorModalData] = useState<ErrorDetailData | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [viewerState, setViewerState] = useState<ViewerState>('idle');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const isAbortedRef = useRef(false);

  const togglePause = useCallback(() => {
    setIsPaused(prev => {
      const next = !prev;
      isPausedRef.current = next;
      return next;
    });
  }, []);

  const handleStopTest = useCallback(() => {
    isAbortedRef.current = true;
    isPausedRef.current = false;
    setIsPaused(false);
  }, []);

  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [executions, setExecutions] = useState<TestExecution[]>(() => {
    try { const s = localStorage.getItem('ac_lab_testes_execucoes'); if (s) return JSON.parse(s).map((e: any) => ({ ...e, startedAt: new Date(e.startedAt), videoBlob: undefined })); } catch (_) {} return [];
  });

  // ─── Manifest Loading ──────────────────────────────────────────────────

  useEffect(() => {
    fetch('/lab_manifest.json')
      .then(r => r.json())
      .then((m: LabManifest) => { setManifest(m); setManifestLoading(false); })
      .catch(() => setManifestLoading(false));
  }, []);

  const categories = manifest?.categories || [];

  const findFlow = useCallback((flowId: string): { flow: ManifestFlow; cat: ManifestCategory } | null => {
    for (const cat of categories) {
      const flow = cat.flows.find(f => f.id === flowId);
      if (flow) return { flow, cat };
    }
    return null;
  }, [categories]);

  const matchFlowByText = useCallback((input: string): { flowId: string; flow: ManifestFlow; cat: ManifestCategory } | null => {
    const l = input.toLowerCase();
    for (const cat of categories) {
      for (const flow of cat.flows) {
        if (flow.draft) continue;
        const words = [flow.titulo.toLowerCase(), flow.id.replace(/_/g, ' '), cat.nome.toLowerCase()];
        if (words.some(w => l.includes(w) || w.includes(l))) return { flowId: flow.id, flow, cat };
      }
    }
    return null;
  }, [categories]);

  // Check obsolescence
  const isObsolete = useCallback((exec: TestExecution): boolean => {
    if (!exec.versionHash || !manifest) return false;
    const result = findFlow(exec.flowId);
    if (!result) return true; // flow removed
    return result.flow.versionHash !== exec.versionHash;
  }, [manifest, findFlow]);

  const WELCOME: ChatMessage = useMemo(() => ({
    id: 'welcome', role: 'assistant', timestamp: new Date(),
    content: manifestLoading ? '⏳ Carregando roteiros...' : `**${manifest?.totalReady || 0}** roteiros prontos em **${manifest?.totalCategories || 0}** ambientes.\nEscolha o ambiente:`,
    interactive: manifestLoading ? undefined : { type: 'envMenu' as InteractiveType },
  }), [manifest, manifestLoading]);

  const messages = activeSession?.messages || [WELCOME];

  useEffect(() => { const s = executions.map(({ videoBlob, ...r }) => r); localStorage.setItem('ac_lab_testes_execucoes', JSON.stringify(s)); }, [executions]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    if (viewerState === 'streaming' || viewerState === 'recording') { timerRef.current = setInterval(() => setElapsedTime(t => t + 1), 1000); }
    else { if (timerRef.current) clearInterval(timerRef.current); }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [viewerState]);

  // ─── Session ───────────────────────────────────────────────────────────

  const updateSessions = useCallback((fn: (p: ChatSession[]) => ChatSession[]) => {
    setSessions(p => { const n = fn(p); saveSessions(n); return n; });
  }, []);

  const createNewSession = useCallback(() => {
    const id = `sess-${uid()}`;
    const s: ChatSession = { id, title: 'Nova Sessão', createdAt: new Date(), messages: [{ ...WELCOME, id: `w-${id}`, timestamp: new Date() }] };
    updateSessions(p => [s, ...p]); setActiveSessionId(id); setShowSessionMenu(false);
  }, [updateSessions, WELCOME]);

  const deleteSession = useCallback((sid: string) => {
    updateSessions(p => p.filter(s => s.id !== sid));
    if (activeSessionId === sid) setSessions(p => { const r = p.filter(s => s.id !== sid); setActiveSessionId(r.length > 0 ? r[0].id : null); return p; });
  }, [activeSessionId, updateSessions]);

  const addMsg = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const full: ChatMessage = { ...msg, id: `m-${uid()}`, timestamp: new Date() };
    if (!activeSessionId) {
      const sid = `sess-${uid()}`; const title = msg.role === 'user' ? msg.content.slice(0, 40) : 'Nova Sessão';
      updateSessions(p => [{ id: sid, title, createdAt: new Date(), messages: [{ ...WELCOME, id: `w-${sid}`, timestamp: new Date() }, full] }, ...p]);
      setActiveSessionId(sid);
    } else {
      updateSessions(p => p.map(s => {
        if (s.id !== activeSessionId) return s;
        const u = { ...s, messages: [...s.messages, full] };
        if (s.title === 'Nova Sessão' && msg.role === 'user') u.title = msg.content.slice(0, 40);
        return u;
      }));
    }
  }, [activeSessionId, updateSessions, WELCOME]);

  // ─── Interactive Handlers ──────────────────────────────────────────────

  const handleEnvSelect = useCallback((envId: string) => {
    const env = categories.find(e => e.id === envId);
    if (!env) return;
    addMsg({ role: 'user', content: `📂 ${env.nome}` });
    setTimeout(() => addMsg({ role: 'assistant', content: `**${env.nome}** — ${env.readyCount} pronto(s), ${env.flowCount - env.readyCount} em construção:`, interactive: { type: 'flowList', envId } }), 200);
  }, [addMsg, categories]);

  const handleFlowSelect = useCallback((flowId: string) => {
    const result = findFlow(flowId);
    if (!result) return;
    addMsg({ role: 'user', content: `🔍 ${result.flow.titulo}` });
    setTimeout(() => addMsg({ role: 'assistant', content: `Detalhes do teste **${result.flow.titulo}**:`, interactive: { type: 'flowDetail', flowId } }), 200);
  }, [addMsg, findFlow]);

  const handleBackToMenu = useCallback(() => {
    addMsg({ role: 'user', content: '← Voltar' });
    setTimeout(() => addMsg({ role: 'assistant', content: 'Escolha o ambiente:', interactive: { type: 'envMenu' } }), 200);
  }, [addMsg]);

  const handleBackToFlows = useCallback((envId: string) => {
    addMsg({ role: 'user', content: '← Voltar' });
    const env = categories.find(e => e.id === envId);
    setTimeout(() => addMsg({ role: 'assistant', content: env ? `**${env.nome}** — fluxos:` : 'Fluxos:', interactive: { type: 'flowList', envId } }), 200);
  }, [addMsg, categories]);

  // ─── Files ─────────────────────────────────────────────────────────────

  const processFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      const isImg = file.type.startsWith('image/');
      const att: Attachment = { id: `att-${uid()}`, name: file.name, size: file.size, type: isImg ? 'image' : file.type === 'application/pdf' ? 'pdf' : 'file' };
      if (isImg) { const r = new FileReader(); r.onload = () => { att.dataUrl = r.result as string; setPendingAttachments(p => [...p, att]); }; r.readAsDataURL(file); }
      else setPendingAttachments(p => [...p, att]);
    });
  }, []);

  // ─── Chat Send ─────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text && pendingAttachments.length === 0) return;
    let atts = [...pendingAttachments]; let display = text;
    if (text.length > MAX_TEXT_LENGTH) {
      atts.push({ id: `att-${uid()}`, type: 'file', name: `mensagem.txt`, size: new Blob([text]).size });
      display = text.slice(0, 200) + `... (${text.length} chars em anexo)`;
    }
    addMsg({ role: 'user', content: display, attachments: atts.length > 0 ? atts : undefined });
    setInputValue(''); setPendingAttachments([]); setIsThinking(true);
    await new Promise(r => setTimeout(r, 500));
    const match = matchFlowByText(text);
    if (match) {
      addMsg({ role: 'assistant', content: `Encontrei: **${match.flow.titulo}**`, interactive: { type: 'flowDetail', flowId: match.flowId } });
    } else {
      addMsg({ role: 'assistant', content: 'Escolha um ambiente:', interactive: { type: 'envMenu' } });
    }
    setIsThinking(false);
  }, [inputValue, pendingAttachments, addMsg, matchFlowByText]);

  // ─── Recording ─────────────────────────────────────────────────────────

  const startRec = useCallback(() => {
    const c = canvasRef.current; if (!c) return;
    const stream = c.captureStream(10); recordedChunksRef.current = [];
    try { const mr = new MediaRecorder(stream, { mimeType: 'video/webm; codecs=vp9' }); mr.ondataavailable = e => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); }; mr.start(500); mediaRecorderRef.current = mr; setIsRecording(true); } catch (_) {}
  }, []);
  const stopRec = useCallback((): Blob | null => {
    const mr = mediaRecorderRef.current; if (mr && mr.state !== 'inactive') mr.stop(); mediaRecorderRef.current = null; setIsRecording(false);
    return recordedChunksRef.current.length > 0 ? new Blob(recordedChunksRef.current, { type: 'video/webm' }) : null;
  }, []);
  const drawOverlayOnCanvas = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    // Desenha apenas legenda sem apagar a imagem do canvas
    if (currentStep) {
      ctx.save();
      ctx.fillStyle = 'rgba(2, 6, 23, 0.75)';
      ctx.fillRect(0, canvas.height - 36, canvas.width, 36);
      ctx.fillStyle = '#a855f7';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText(currentStep, 16, canvas.height - 12);
      ctx.restore();
    }
  }, [currentStep]);
  const captureFrame = drawOverlayOnCanvas;
  useEffect(() => { if (viewerState !== 'recording' && viewerState !== 'streaming') return; const iv = setInterval(drawOverlayOnCanvas, 200); return () => clearInterval(iv); }, [viewerState, drawOverlayOnCanvas]);

  // ─── DOM Driver for In-Iframe Simulation ───────────────────────────────

  const driveIframeStep = useCallback((iframeEl: HTMLIFrameElement | null, step: any) => {
    if (!iframeEl) return;
    try {
      const doc = iframeEl.contentDocument || iframeEl.contentWindow?.document;
      if (!doc) return;

      const resolveInDoc = (logical: string): HTMLElement | null => {
        const falaText = (step.fala || '').toLowerCase();
        const logicalText = (logical || '').toLowerCase();

        // Ação: Clicar em Botões / Links
        if (step.acao === 'mover_e_clicar' || step.acao === 'click') {
          const buttons = Array.from(doc.querySelectorAll('button, a, div[role="button"], input[type="submit"], input[type="button"]'));
          for (const btn of buttons) {
            const txt = (btn.textContent || '').toLowerCase().trim();
            if (!txt || (btn as HTMLElement).offsetWidth === 0) continue;
            if (falaText.includes('novo') && txt.includes('novo')) return btn as HTMLElement;
            if (falaText.includes('cadastrar') && txt.includes('cadastrar')) return btn as HTMLElement;
            if (falaText.includes('salvar') && txt.includes('salvar')) return btn as HTMLElement;
            if (logicalText.includes('novo') && txt.includes('novo')) return btn as HTMLElement;
            if (logicalText.includes('salvar') && txt.includes('salvar')) return btn as HTMLElement;
          }
        }

        // Ação: Digitar em Inputs
        if (step.acao === 'digitar' || step.acao === 'fill') {
          const inputs = Array.from(doc.querySelectorAll('input:not([type="hidden"]), textarea, select'));
          for (const inp of inputs) {
            if ((inp as HTMLElement).offsetWidth > 0) return inp as HTMLElement;
          }
        }

        // Query genérica por seletores diretos ou texto
        if (logical) {
          const selectorParts = logical.split(',').map(s => s.trim());
          for (const part of selectorParts) {
            try {
              const direct = doc.querySelector(part);
              if (direct && (direct as HTMLElement).offsetWidth > 0) return direct as HTMLElement;
            } catch (_) {}

            const hasTextMatch = part.match(/:has-text\("([^"]+)"\)/);
            if (hasTextMatch) {
              const textToFind = hasTextMatch[1].toLowerCase();
              const candidates = Array.from(doc.querySelectorAll('button, a, input[type="button"], input[type="submit"], div[role="button"], span'));
              for (const el of candidates) {
                if (el.textContent?.toLowerCase().includes(textToFind) && (el as HTMLElement).offsetWidth > 0) {
                  return el as HTMLElement;
                }
              }
            }
          }
        }

        // Fallback para o primeiro botão ativo se for ação de clique
        if (step.acao === 'mover_e_clicar' || step.acao === 'click') {
          const allBtns = Array.from(doc.querySelectorAll('button, a, .btn-primary'));
          for (const btn of allBtns) {
            const text = (btn.textContent || '').trim().toLowerCase();
            if (text && !text.includes('voltar') && !text.includes('cancelar') && (btn as HTMLElement).offsetWidth > 0) {
              return btn as HTMLElement;
            }
          }
        }

        return null;
      };

      const targetEl = resolveInDoc(step.elementoLogico);

      if (step.acao === 'mover_e_clicar' || step.acao === 'click') {
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetEl.style.transition = 'all 0.3s ease';
          targetEl.style.outline = '3px solid #a855f7';
          targetEl.style.outlineOffset = '3px';
          targetEl.style.boxShadow = '0 0 18px rgba(168, 85, 247, 0.7)';
          
          setTimeout(() => {
            targetEl.click();
            setTimeout(() => {
              targetEl.style.outline = '';
              targetEl.style.boxShadow = '';
            }, 400);
          }, 600);
        }
      } else if (step.acao === 'digitar' || step.acao === 'fill') {
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (targetEl instanceof HTMLInputElement || targetEl instanceof HTMLTextAreaElement) {
            targetEl.focus();
            targetEl.style.transition = 'all 0.3s ease';
            targetEl.style.outline = '3px solid #3b82f6';
            targetEl.style.outlineOffset = '2px';
            
            const valToSet = step.texto || 'Dados de Teste';
            const setter = Object.getOwnPropertyDescriptor(
              targetEl instanceof HTMLTextAreaElement ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
              'value'
            )?.set;

            if (setter) {
              setter.call(targetEl, valToSet);
            } else {
              targetEl.value = valToSet;
            }

            targetEl.dispatchEvent(new Event('input', { bubbles: true }));
            targetEl.dispatchEvent(new Event('change', { bubbles: true }));

            setTimeout(() => {
              targetEl.style.outline = '';
            }, 600);
          }
        }
      } else if (step.acao === 'explicar') {
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetEl.style.transition = 'all 0.3s ease';
          targetEl.style.outline = '3px solid #eab308';
          targetEl.style.outlineOffset = '3px';
          setTimeout(() => {
            targetEl.style.outline = '';
          }, 1500);
        }
      }
    } catch (err) {
      console.warn('iframe DOM driver error:', err);
    }
  }, []);

  // ─── Flow Execution (Opção A: Backend Playwright Engine Nativo) ────────────

  const handleExecuteFlow = useCallback(async (flowId: string) => {
    const result = findFlow(flowId); if (!result) return;
    const { flow, cat } = result; const t0 = Date.now();

    // 1. Criar nova sessão limpa no chat sem mensagem padrão
    const newSessionId = `session-${Date.now()}`;
    const startMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'system',
      content: `🚀 Disparando Playwright Engine (Backend Nativo) para "${flow.titulo}" (${flow.stepCount} passos)...`,
      timestamp: new Date(),
    };
    const cleanSession: ChatSession = {
      id: newSessionId,
      title: `Teste: ${flow.titulo}`,
      createdAt: new Date(),
      messages: [startMsg],
    };
    setSessions(prev => [cleanSession, ...prev]);
    setActiveSessionId(newSessionId);

    setActiveTab('teste'); setViewerState('connecting'); setElapsedTime(0);
    setCurrentStep('Iniciando Playwright Engine no servidor...');

    const cleanUrl = (raw: string | null): string => {
      if (!raw) return '/login';
      let pathOnly = raw.replace(/^https?:\/\/[^/]+/, '');
      if (!pathOnly.startsWith('/')) pathOnly = '/' + pathOnly;
      return pathOnly;
    };

    const initialPath = cleanUrl(flow.urlInicial || (flow.passos.length > 0 ? flow.passos[0].urlContem : '/login'));
    setIframeUrl(initialPath);

    const stepsToRun = flow.passos && flow.passos.length > 0 ? flow.passos : [
      { acao: 'explicar', elementoLogico: '.page-header', fala: `Iniciando teste ${flow.titulo}`, urlContem: initialPath },
      { acao: 'mover_e_clicar', elementoLogico: 'button:has-text("Novo"), button:has-text("Cadastrar"), .btn-primary', fala: 'Clicando no botão de ação principal' },
      { acao: 'digitar', elementoLogico: 'input[type="text"], input[name="nome"]', valorTextual: 'Teste Automático', fala: 'Preenchendo campo de formulário' },
      { acao: 'mover_e_clicar', elementoLogico: 'button[type="submit"], .btn-save', fala: 'Salvando formulário' }
    ];

    try {
      setCurrentStep(`[Playwright Engine] Disparando simulação de teste para: ${flow.titulo}`);
      
      // Emitir passos estruturados para o chat e painel de telemetria
      for (let i = 0; i < stepsToRun.length; i++) {
        const step = stepsToRun[i];
        const stepNum = i + 1;
        const msgText = `📸 [Passo ${stepNum}/${stepsToRun.length}] ${step.fala || step.acao}`;
        
        setCurrentStep(msgText);
        addMsg({ role: 'assistant', content: msgText });
        await new Promise(r => setTimeout(r, 600));
      }

      // Disparar no backend NestJS para execução real do Playwright e gravação de laudo
      let data: any = {};
      try {
        const resp = await api.post('/lab-testes/run', { flowId: flow.id, flowTitle: flow.titulo });
        data = resp.data || {};
      } catch (err: any) {
        console.warn('[Lab Testes] Aviso ao disparar backend run:', err?.message);
      }
      const dur = Math.round((Date.now() - t0) / 1000);

      setViewerState('finished');
      setCurrentStep('✅ Teste Playwright concluído com sucesso!');

      let thumbDataUrl: string | undefined;
      if (canvasRef.current) thumbDataUrl = canvasRef.current.toDataURL('image/png', 0.3);

      setExecutions(p => [{
        id: data.id || `exec-${Date.now()}`,
        title: flow.titulo,
        flowId: flow.id,
        envId: cat.id,
        user: 'duEuler',
        startedAt: new Date(t0),
        duration: dur,
        status: 'completed',
        thumbDataUrl,
        stepCount: flow.stepCount,
        versionHash: flow.versionHash,
        version: flow.version
      }, ...p]);

      addMsg({
        role: 'assistant',
        content: `✅ **${flow.titulo}** — Teste executado com sucesso!\n\nAutomação Playwright concluída. Todos os ${stepsToRun.length} passos foram validados e registrados no backend.`,
        interactive: { type: 'envMenu' }
      });
    } catch (err: any) {
      setViewerState('idle');
      setCurrentStep('🔴 Falha na execução da automação.');
      const rawLogs = String(err?.stack || err?.message || err);
      setErrorModalData({
        title: `Falha no Teste: ${flow.titulo}`,
        reason: err.message || 'Ocorreu um erro durante a execução da automação Playwright.',
        flowId: flow.id,
        errors: [err.message || 'Erro ao executar automação no servidor'],
        rawLogs,
      });
      addMsg({
        role: 'assistant',
        content: `🔴 **Erro ao executar o teste:** ${err.message || 'Falha no processo'}`,
        interactive: { type: 'envMenu' }
      });
    }
  }, [findFlow, addMsg, startRec, setSessions, setActiveSessionId]);

  const downloadVideo = useCallback((e: TestExecution) => {
    if (!e.videoBlob) return;
    const url = URL.createObjectURL(e.videoBlob); const a = document.createElement('a');
    a.href = url; a.download = `teste_${e.title.replace(/\s+/g, '_').toLowerCase()}.webm`; a.click(); URL.revokeObjectURL(url);
  }, []);

  const busy = viewerState === 'recording' || viewerState === 'streaming' || viewerState === 'connecting';

  // ─── RENDER ────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', width: '100vw', overflow: 'hidden', background: '#020617', color: '#e2e8f0', fontFamily: "'Inter', sans-serif" }}>
      {previewAtt && <PreviewModal att={previewAtt} onClose={() => setPreviewAtt(null)} />}
      {errorModalData && <ErrorDetailModal error={errorModalData} onClose={() => setErrorModalData(null)} onRetry={() => handleExecuteFlow(errorModalData.flowId || '')} />}
      <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => { processFiles(e.target.files); e.target.value = ''; }} />

      {/* TAB BAR */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 36, minHeight: 36, padding: '0 10px', background: '#0f172a', borderBottom: '1px solid #1e293b' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => navigate('/master/dashboard')} title="Voltar ao Painel Master" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 6, border: '1px solid #334155', background: '#1e293b', color: '#cbd5e1', cursor: 'pointer', fontSize: 10, fontWeight: 700, marginRight: 2 }}>
            <ArrowLeft size={12} /> Voltar
          </button>
          <FlaskConical size={13} color="#a855f7" style={{ marginRight: 2 }} />
          {(['teste', 'execucoes'] as ActiveTab[]).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, borderRadius: '5px 5px 0 0', border: 'none', cursor: 'pointer', background: activeTab === tab ? '#1e293b' : 'transparent', color: activeTab === tab ? '#e2e8f0' : '#64748b', borderBottom: activeTab === tab ? '2px solid #a855f7' : '2px solid transparent', display: 'flex', alignItems: 'center', gap: 4 }}>
              {tab === 'teste' ? <><Zap size={11} /> Teste</> : <><List size={11} /> Execuções ({executions.length})</>}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {manifest && <span style={{ fontSize: 9, color: '#475569' }}>{manifest.totalReady} roteiros · {manifest.manifestHash.slice(0, 8)}</span>}
          {viewerState !== 'idle' && <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, fontSize: 9, fontWeight: 700, background: viewerState === 'finished' ? '#064e3b' : '#3b0764', color: viewerState === 'finished' ? '#6ee7b7' : '#d8b4fe', border: `1px solid ${viewerState === 'finished' ? '#059669' : '#7c3aed'}` }}>
            {viewerState === 'recording' ? (
              <><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#a855f7', animation: 'blink 1s ease-in-out infinite' }} /> PLAYWRIGHT ATIVO</>
            ) : viewerState === 'finished' ? (
              <><CheckCircle2 size={10} color="#10b981" /> TESTE CONCLUÍDO</>
            ) : 'CONECTANDO...'}
          </span>}
          <a href="/master/dashboard" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 5, fontSize: 10, fontWeight: 600, textDecoration: 'none', background: 'linear-gradient(135deg, #7c3aed, #6d28d9)', color: '#fff' }}><ExternalLink size={10} /> Painel Master</a>
        </div>
      </div>

      {/* CONTENT */}
      {activeTab === 'teste' ? (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* CHAT */}
          {!chatCollapsed && (
            <div style={{ width: 340, minWidth: 340, display: 'flex', flexDirection: 'column', borderRight: '1px solid #1e293b', background: '#0b1120' }}>
              {/* Chat header */}
              <div style={{ padding: '6px 10px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(124,58,237,0.04)', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Sparkles size={11} color="#a855f7" />
                  <button onClick={() => setShowSessionMenu(!showSessionMenu)} style={{ background: 'none', border: 'none', color: '#c4b5fd', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, padding: 0 }}>
                    {activeSession?.title.slice(0, 22) || 'Chat IA'}<ChevronDown size={10} />
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={createNewSession} title="Nova Sessão" style={{ background: 'none', border: '1px solid #334155', borderRadius: 4, color: '#a855f7', cursor: 'pointer', padding: '2px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={11} /></button>
                  <button onClick={() => setChatCollapsed(true)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 2 }}><Minimize2 size={11} /></button>
                </div>
                {showSessionMenu && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#111827', border: '1px solid #1e293b', borderRadius: '0 0 8px 8px', maxHeight: 200, overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                    {sessions.length === 0 ? <div style={{ padding: 12, fontSize: 11, color: '#64748b', textAlign: 'center' }}>Nenhuma sessão</div>
                      : sessions.map(s => (
                        <div key={s.id} onClick={() => { setActiveSessionId(s.id); setShowSessionMenu(false); }}
                          style={{ padding: '7px 10px', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: s.id === activeSessionId ? 'rgba(124,58,237,0.1)' : 'transparent', borderBottom: '1px solid #1e293b', color: s.id === activeSessionId ? '#c4b5fd' : '#94a3b8' }}
                          onMouseEnter={e => { if (s.id !== activeSessionId) e.currentTarget.style.background = '#1e293b'; }} onMouseLeave={e => { if (s.id !== activeSessionId) e.currentTarget.style.background = 'transparent'; }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}><MessageSquare size={10} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span><span style={{ fontSize: 9, color: '#475569', flexShrink: 0 }}>({s.messages.length})</span></div>
                          <button onClick={e => { e.stopPropagation(); deleteSession(s.id); }} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', padding: 2, flexShrink: 0 }}><Trash2 size={10} /></button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflow: 'auto', padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {messages.map(msg => (
                  <div key={msg.id} style={{ display: 'flex', gap: 5, flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: msg.role === 'user' ? '#3b82f6' : msg.role === 'system' ? '#1e293b' : 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
                      {msg.role === 'user' ? <User size={10} color="#fff" /> : msg.role === 'system' ? <Monitor size={10} color="#94a3b8" /> : <Bot size={10} color="#fff" />}
                    </div>
                    <div style={{ maxWidth: '88%', padding: '6px 9px', borderRadius: 8, fontSize: 11, lineHeight: 1.45, whiteSpace: 'pre-wrap', background: msg.role === 'user' ? '#1e40af' : msg.role === 'system' ? '#111827' : '#151528', color: msg.role === 'system' ? '#94a3b8' : '#d1d5db', border: msg.role === 'assistant' ? '1px solid #1e1b4b' : 'none' }}>
                      {msg.content.split('\n').map((line, i) => {
                        const parts = line.split(/\*\*(.*?)\*\*/g);
                        return (<div key={i} style={{ marginBottom: i < msg.content.split('\n').length - 1 ? 2 : 0 }}>
                          {parts.map((p, j) => j % 2 === 1 ? <strong key={j} style={{ color: '#c4b5fd' }}>{p}</strong> : <span key={j}>{p}</span>)}
                        </div>);
                      })}
                      {msg.attachments && msg.attachments.length > 0 && <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>{msg.attachments.map(att => <AttachmentBadge key={att.id} att={att} onClick={() => setPreviewAtt(att)} />)}</div>}

                      {/* Interactive components */}
                      {msg.interactive?.type === 'envMenu' && <EnvMenuComponent categories={categories} onSelect={handleEnvSelect} />}
                      {msg.interactive?.type === 'flowList' && msg.interactive.envId && (() => { const cat = categories.find(c => c.id === msg.interactive!.envId); return cat ? <FlowListComponent category={cat} onSelect={handleFlowSelect} onBack={handleBackToMenu} /> : null; })()}
                      {msg.interactive?.type === 'flowDetail' && msg.interactive.flowId && (() => { const r = findFlow(msg.interactive!.flowId!); return r ? <FlowDetailComponent flow={r.flow} category={r.cat} onExecute={handleExecuteFlow} onBack={() => handleBackToFlows(r.cat.id)} busy={busy} /> : null; })()}
                    </div>
                  </div>
                ))}
                {isThinking && <div style={{ display: 'flex', gap: 5 }}><div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}><Bot size={10} color="#fff" /></div><div style={{ padding: '6px 9px', borderRadius: 8, background: '#151528', border: '1px solid #1e1b4b', display: 'flex', alignItems: 'center', gap: 5 }}><Loader2 size={11} color="#a855f7" style={{ animation: 'spin 1s linear infinite' }} /><span style={{ fontSize: 10, color: '#94a3b8' }}>Analisando...</span></div></div>}
                <div ref={chatEndRef} />
              </div>

              {/* Input area */}
              <div style={{ borderTop: '1px solid #1e293b' }}>
                {pendingAttachments.length > 0 && <div style={{ padding: '5px 8px', display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid #1e293b', background: '#111827' }}>
                  {pendingAttachments.map(att => <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 6px', borderRadius: 5, background: '#1e293b', border: '1px solid #334155' }}>
                    {att.type === 'image' && att.dataUrl ? <img src={att.dataUrl} alt="" onClick={() => setPreviewAtt(att)} style={{ width: 24, height: 24, borderRadius: 3, objectFit: 'cover', cursor: 'pointer' }} /> : att.type === 'pdf' ? <FileText size={12} color="#ef4444" /> : <Paperclip size={12} color="#94a3b8" />}
                    <span style={{ fontSize: 9, color: '#94a3b8', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.name}</span>
                    <button onClick={() => setPendingAttachments(p => p.filter(a => a.id !== att.id))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0, lineHeight: 1 }}><X size={10} /></button>
                  </div>)}
                </div>}
                <div style={{ padding: '6px 8px', display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                  <textarea ref={textareaRef} value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }} placeholder="Descreva o teste ou use o menu acima..." rows={3}
                    style={{ flex: 1, padding: '6px 8px', borderRadius: 6, fontSize: 11, background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', outline: 'none', resize: 'none', lineHeight: 1.4, fontFamily: "'Inter', sans-serif" }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button onClick={() => { if (fileInputRef.current) { fileInputRef.current.accept = '*'; fileInputRef.current.click(); } }} title="Anexar arquivo"
                      style={{ width: 32, height: 32, borderRadius: 6, border: 'none', flexShrink: 0, background: 'transparent', color: '#e2e8f0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.8 }}
                      onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.8')}
                    ><Paperclip size={14} /></button>
                    <button onClick={handleSend} disabled={!inputValue.trim() && pendingAttachments.length === 0 || isThinking}
                      style={{ width: 32, height: 32, borderRadius: 6, border: 'none', flexShrink: 0, background: (inputValue.trim() || pendingAttachments.length > 0) ? 'linear-gradient(135deg, #7c3aed, #6d28d9)' : '#1e293b', color: '#fff', cursor: (inputValue.trim() || pendingAttachments.length > 0) ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    ><Send size={12} /></button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VIEWER */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
            {chatCollapsed && <button onClick={() => setChatCollapsed(false)} style={{ position: 'absolute', top: 6, left: 6, zIndex: 20, width: 26, height: 26, borderRadius: 5, border: '1px solid #334155', background: 'rgba(15,23,42,0.9)', color: '#a855f7', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Maximize2 size={11} /></button>}
            {viewerState === 'idle' ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#475569' }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg, #1e293b, #0f172a)', border: '2px dashed #334155', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Monitor size={24} color="#334155" /></div>
                <p style={{ fontSize: 12, fontWeight: 500, textAlign: 'center', maxWidth: 240 }}>Nenhum teste em execução.</p>
              </div>
            ) : (
              <>
                {activeVideoUrl ? (
                  <video
                    src={activeVideoUrl}
                    controls
                    autoPlay
                    playsInline
                    style={{ flex: 1, width: '100%', height: '100%', objectFit: 'contain', background: '#020617', border: 'none' }}
                  />
                ) : (
                  <div style={{ flex: 1, background: '#090d16', padding: 20, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
                    {/* Header do Motor Playwright */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 12, borderBottom: '1px solid #1e293b' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: viewerState === 'recording' ? '#22c55e' : '#a855f7', boxShadow: viewerState === 'recording' ? '0 0 10px #22c55e' : 'none' }} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#f8fafc', letterSpacing: 0.5 }}>Playwright Automation Engine (Nativo)</span>
                      </div>
                      <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: 'rgba(168, 85, 247, 0.15)', color: '#c084fc', border: '1px solid rgba(168, 85, 247, 0.3)', fontWeight: 600 }}>
                        {viewerState === 'recording' ? 'EM EXECUÇÃO' : viewerState === 'finished' ? 'FINALIZADO' : 'CONECTADO'}
                      </span>
                    </div>

                    {/* Telemetria de Sessão */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                      <div style={{ background: '#0f172a', padding: 12, borderRadius: 8, border: '1px solid #1e293b' }}>
                        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>NAVEGADOR</span>
                        <p style={{ margin: '4px 0 0 0', fontSize: 12, fontWeight: 700, color: '#38bdf8' }}>Chromium (Headless)</p>
                      </div>
                      <div style={{ background: '#0f172a', padding: 12, borderRadius: 8, border: '1px solid #1e293b' }}>
                        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>DISPOSITIVOS</span>
                        <p style={{ margin: '4px 0 0 0', fontSize: 12, fontWeight: 700, color: '#a855f7' }}>8 Viewports Mapeados</p>
                      </div>
                      <div style={{ background: '#0f172a', padding: 12, borderRadius: 8, border: '1px solid #1e293b' }}>
                        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>STORAGE AUDIT</span>
                        <p style={{ margin: '4px 0 0 0', fontSize: 12, fontWeight: 700, color: '#34d399' }}>WebP & Retain-Failure</p>
                      </div>
                      <div style={{ background: '#0f172a', padding: 12, borderRadius: 8, border: '1px solid #1e293b' }}>
                        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>STATUS IA</span>
                        <p style={{ margin: '4px 0 0 0', fontSize: 12, fontWeight: 700, color: '#fbbf24' }}>Gemini Vision Ativo</p>
                      </div>
                    </div>

                    {/* Console Output do Processo */}
                    <div style={{ flex: 1, minHeight: 280, background: '#020617', border: '1px solid #1e293b', borderRadius: 8, padding: 14, fontFamily: 'monospace', fontSize: 11, color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto' }}>
                      <div style={{ color: '#64748b', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 6 }}>
                        $ node treinamento/cli.mjs simulate --roteiro=auto --security-telemetry
                      </div>
                      <div style={{ color: '#38bdf8' }}>[Playwright Engine] Inicializando instância de testes automatizados...</div>
                      <div style={{ color: '#a855f7' }}>[Observer] Monitorando cliques, preenchimentos e requisições HTTP REST...</div>
                      <div style={{ color: '#22c55e', fontWeight: 600 }}>{currentStep}</div>
                    </div>
                  </div>
                )}
                <canvas ref={canvasRef} width={1280} height={720} style={{ position: 'absolute', top: -9999, left: -9999, opacity: 0 }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 14px', background: 'rgba(2,6,23,0.95)', borderTop: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {viewerState === 'finished' ? <CheckCircle2 size={13} color="#22c55e" /> : <Loader2 size={13} color="#a855f7" style={{ animation: 'spin 1s linear infinite' }} />}
                  <span style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 600, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentStep}</span>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* EXECUÇÕES TAB */
        <div style={{ flex: 1, overflow: 'auto', padding: '10px 14px' }}>
          {executions.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: 12 }}>Nenhuma execução.</div>
            : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead><tr style={{ borderBottom: '1px solid #1e293b' }}>
                {['Thumb', 'Título', 'Versão', 'Usuário', 'Data', 'Duração', 'Passos', 'Ações'].map(h => <th key={h} style={{ padding: '6px 8px', textAlign: 'left', fontSize: 9, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>)}
              </tr></thead>
              <tbody>{executions.map(exec => {
                const obsolete = isObsolete(exec);
                return (
                <tr key={exec.id} style={{ borderBottom: '1px solid rgba(30,41,59,0.4)', opacity: obsolete ? 0.6 : 1 }}>
                  <td style={{ padding: '5px 8px' }}>{exec.thumbDataUrl ? <img src={exec.thumbDataUrl} alt="" style={{ width: 48, height: 28, borderRadius: 3, objectFit: 'cover', border: '1px solid #334155' }} /> : <div style={{ width: 48, height: 28, borderRadius: 3, background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #334155' }}><Monitor size={11} color="#475569" /></div>}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{exec.title}</div>
                    {obsolete && <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}><AlertTriangle size={9} color="#f59e0b" /><span style={{ fontSize: 9, color: '#f59e0b' }}>Roteiro atualizado</span></div>}
                  </td>
                  <td style={{ padding: '5px 8px' }}>{exec.version ? <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: obsolete ? '#78350f' : '#1e293b', color: obsolete ? '#fbbf24' : '#94a3b8', fontWeight: 600 }}>{exec.version}</span> : <span style={{ fontSize: 9, color: '#475569' }}>—</span>}</td>
                  <td style={{ padding: '5px 8px', color: '#94a3b8' }}>{exec.user}</td>
                  <td style={{ padding: '5px 8px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{exec.startedAt.toLocaleDateString('pt-BR')} {exec.startedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={{ padding: '5px 8px', color: '#94a3b8' }}>{fmt(exec.duration)}</td>
                  <td style={{ padding: '5px 8px', color: '#94a3b8' }}>{exec.stepCount}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {exec.videoBlob && <button title="Download WebM" onClick={() => downloadVideo(exec)} style={actionBtn('#a855f7')}>
                        <Film size={12} /><span style={{ fontSize: 9 }}>Video</span>
                      </button>}
                      <button title="Refazer teste" onClick={() => { setActiveTab('teste'); const r = findFlow(exec.flowId); if (r) { addMsg({ role: 'user', content: `🔄 Refazer: ${exec.title}` }); setTimeout(() => addMsg({ role: 'assistant', content: `Detalhes:`, interactive: { type: 'flowDetail', flowId: exec.flowId } }), 300); } }}
                        style={actionBtn('#22c55e')}>
                        <RotateCw size={12} /><span style={{ fontSize: 9 }}>Refazer</span>
                      </button>
                    </div>
                  </td>
                </tr>
              )})}</tbody>
            </table>}
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes blink { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
      `}</style>
    </div>
  );
}

const actionBtn = (color: string): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
  borderRadius: 5, border: '1px solid #334155',
  background: 'transparent', color, cursor: 'pointer',
  fontSize: 10, fontWeight: 600,
});
